// Dependencies
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { faArrowLeft, faPen, faPlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { toast } from "@performyard/py-components";

import { AdminTitle } from "../../../../../../../components/Admin";
import Button from "../../../../../../../components/Button";
import TextInput from "../../../../../../../components/Inputs/InputText";
import Link from "../../../../../../../components/Link";
import Spacer from "../../../../../../../components/Spacer";
import Text from "../../../../../../../components/Text";

import useDebouncedValue from "../../../../../../../hooks/useDebouncedValue";

// API
import {
	createUserSelectionFromCohort,
	fetchNewSelectionObject,
	updateCohort,
} from "../../../../../../../api/services";

// Selectors
import { getURLParam } from "../../../../../../../selectors/navigation";

// Components
import Directory from "../../../../Directory";
// Styles
import css from "../StaticEmployees/StaticEmployees.module.scss";

declare interface CohortEmployeesDirectoryProps {
	cohort?: {
		name: string;
		description: string;
		type: string;
	};
	backUrl: string;
}

/**
 * In order to remove a user from a cohort, we need to display a list of
 * employees that are currently in the cohort. We do this by using the
 * limitSelected parameter of the directory api. Every user that is checked is
 * added to a new user selection object -- called the "staging" selection. When
 * the user is done, we pull all users in the new selection object.
 * Since this is currently a single select delete, when clicking the delete button, we add the single user, remove them
 * then pull the new list of users.
 * @param props
 * @returns
 */
const CohortEmployeesDirectory = ({
	cohort = { name: "", description: " ", type: "static" },
	backUrl,
}: CohortEmployeesDirectoryProps) => {
	const cohortID = useSelector((state) => getURLParam(state, "cohortID"));

	const [search, setSearch] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const debouncedSearch = useDebouncedValue(search, 250);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [userSelectionIDs, setUserSelectionIDs] = useState<{ current: string; staging: string } | null>(null);

	const fetchCohortUserSelection = async () => {
		if (cohortID && isUpdating === false) {
			const [current, staging] = await Promise.all([
				createUserSelectionFromCohort(cohortID),
				fetchNewSelectionObject(),
			]);

			setUserSelectionIDs({
				current: current.user_selection,
				staging: staging.user_selection_id,
			});
		}
	};

	useEffect(() => {
		fetchCohortUserSelection();
	}, [cohortID]);

	const handleRemoveUsersFromCohort = async () => {
		setIsUpdating(true);
		try {
			await updateCohort(cohortID, {
				removeUserSelectionID: userSelectionIDs?.staging,
			});
			toast.success("Employee removed from Cohort");
			await fetchCohortUserSelection();
			setSelectedEmployees([]);
		} catch (err) {
			toast.error("Unable to remove employee from cohort. Please try again.");
		} finally {
			setIsUpdating(false);
		}
	};

	const emptyText =
		cohort.type === "static"
			? "The Cohort has no currently selected employees."
			: "The defined condition results in no employees.";

	const isLoading = userSelectionIDs === null || isUpdating;
	return (
		<>
			<AdminTitle includeSpacer={false}>Employee Cohorts</AdminTitle>
			<Spacer height={4} />
			<div className={css.main}>
				<div className={css.detailsContainer}>
					<div className={css.backButtonContainer}>
						<Link overrideStyles={true} href={backUrl} className={css.backButton}>
							<FontAwesomeIcon icon={faArrowLeft} data-testid="arrow-left" />{" "}
							<Text size="paragraph-3" weight="semi-bold">
								Back
							</Text>
						</Link>
					</div>
					<div className={css.info}>
						<div className={css.nameContainer}>
							<Link
								overrideStyles={true}
								href={`/administration/engagement/cohorts/${cohortID}/details`}
								className={css.editLink}
							>
								<Text className={css.name} size="paragraph-1" weight="semi-bold" color="neutral-9">
									{cohort?.name}
								</Text>
								<div className={css.editButton}>
									<FontAwesomeIcon icon={faPen} data-testid="pen" />
								</div>
							</Link>
						</div>
						<Spacer height={1} />
						{cohort?.description ? (
							<Text size="caption" weight="regular" color="neutral-5" className={css.description}>
								{cohort.description}
							</Text>
						) : (
							<Spacer height={2} />
						)}
					</div>
				</div>

				<Spacer height={4} />
				<div className={css.directoryWrapper}>
					<div className={css.controls}>
						<div className={css.search}>
							<TextInput
								icon={faSearch}
								value={search}
								onChange={setSearch}
								placeholder="Search Employees..."
								ariaLabel="Search employees"
								clearable
								testId="search-cohort-employee-directory"
							/>
						</div>
						<div />
						<div>
							<Button
								href={`/administration/engagement/cohorts/${cohortID}/add-employees`}
								color="blue"
								icon={faPlus}
								iconPosition="left"
							>
								{cohort.type === "static" ? "Add Members" : "Edit Conditions"}
							</Button>
						</div>
					</div>
					<Spacer height={2} />
					<Directory
						columns={[]}
						stagingUserSelectionID={userSelectionIDs?.staging}
						selectedEmployees={selectedEmployees}
						setSelectedEmployees={setSelectedEmployees}
						employeeSearchProps={{
							searchTerm: debouncedSearch,
							userSelectionID: userSelectionIDs?.current,
							limitSelected: true,
						}}
						removeUsersFromCohort={handleRemoveUsersFromCohort}
						canSingleDelete={cohort.type === "static"}
						isLoading={isLoading}
						nameColumnTitle={`${cohort.name} Members`}
						emptyText={debouncedSearch ? "A search on the employee list results in no matches." : emptyText}
					/>
				</div>
			</div>
		</>
	);
};

export default CohortEmployeesDirectory;
